/* Bespoke CTA garments. Each CTA owns its own interaction identity:
   seal stamp (primary), route link (secondary), viewfinder (WhatsApp),
   band link (division rows), ledger link (markets). No shared button style. */
import { Link } from "./link";
import type { ReactNode } from "react";

import { WHATSAPP_NUMBER } from "@/content/site";

export function SealButton({
  to,
  href,
  children,
  small,
  type,
  disabled,
}: {
  to?: string;
  href?: string;
  children: ReactNode;
  small?: boolean;
  type?: "submit";
  disabled?: boolean;
}) {
  const cls = `im-seal-btn${small ? " im-seal-btn--sm" : ""}`;
  if (type === "submit") {
    return (
      <button className={cls} type="submit" disabled={disabled}>
        {children}
      </button>
    );
  }
  if (href) {
    return (
      <a className={cls} href={href}>
        {children}
      </a>
    );
  }
  return (
    <Link className={cls} to={to ?? "/"}>
      {children}
    </Link>
  );
}

export function RouteLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="im-route-link" to={to}>
      <svg aria-hidden="true" viewBox="0 0 84 18">
        <path className="route-path" d="M0 9 C 20 9, 24 2, 40 2 S 60 16, 84 16" />
        <circle className="route-dot" r="3" />
      </svg>
      <span>{children}</span>
    </Link>
  );
}

export function WhatsAppLink({ label, fixed, text }: { label: string; fixed?: boolean; text?: string }) {
  const url = `https://wa.me/${WHATSAPP_NUMBER}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
  return (
    <a
      className={`im-viewfinder${fixed ? " im-viewfinder--fixed" : ""}`}
      href={url}
      rel="noreferrer"
      target="_blank"
      aria-label={label}
    >
      <i aria-hidden="true" />
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M4 20l1.3-3.9A8 8 0 1 1 8.4 19L4 20z" />
        <path d="M9 9.5c0 3 2.5 5.5 5.5 5.5l1-1.5-1.8-.9-.8.8c-1-.4-1.9-1.3-2.3-2.3l.8-.8L10.5 8.5 9 9.5z" />
      </svg>
      <span>{label}</span>
    </a>
  );
}

export function BandLink({ to, children, meta }: { to: string; children: ReactNode; meta: string }) {
  return (
    <Link className="im-band-link" to={to}>
      <span>{children}</span>
      <span>{meta}</span>
    </Link>
  );
}

export function LedgerLink({
  to,
  index,
  title,
  sub,
}: {
  to: string;
  index: string;
  title: string;
  sub?: string;
}) {
  return (
    <Link className="im-ledger-link" to={to}>
      <b>{index}</b>
      <strong>{title}</strong>
      <em aria-hidden="true">→</em>
      {sub ? <small>{sub}</small> : null}
    </Link>
  );
}
